import { Injectable } from '@angular/core';
import { BehaviorSubject, debounceTime, Subject } from 'rxjs';
import { CVApiService } from 'src/app/reusables/services/cv-api.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';

type Challenge = {
  ruleID: string,
  startDate: string,
  wager: number,
  invitees: number[],
  challengeType: number;
  ships: {
    ShipSize: number;
    ShipType: number;
    StartColumn: number;
    StartRow: number;
    IsVertical: number;
  }[]
}

export type BoardShip = {
  id: TSHIPS_ID;
  size: number;
  orientation: TOrientation;
  isPlaced: boolean;
};

export type TPlayer = {
  ChallengeID: number;
  GameStatus: number;
  Hits: number;
  ShotsAvailable: number;
  LoginID: number;
};

export type ShotPlaceholder = {
  shot: boolean;
  hit: boolean;
  sunk: boolean;
  mark: boolean;
};

export type ShotData = {
  ShotId: number;
  PlayerId: number;
  TargetPlayerId: number;
  _row: number;
  _column: number;
  Hit: boolean;
}

export const ShotStatusValues = {
  unset: '',
  0: 'Miss',
  1: 'Hit',
  2: 'Error',
} as const;

export type ShotStatus = {
  status: keyof typeof ShotStatusValues;
  message: string;
};

export const BattleBoatPlayerStatus = {
    READY: 1,
    IN_PROGRESS: 2,
    VICTORY: 3,
    DEFEAT: 4,
    DRAW: 5,
    PAID: 6,
    RETURNED: 7
}

export type TSHIPS_ID = keyof typeof ShipTypeObj;
export type TOrientation = keyof typeof ORIENTATIONS;


@Injectable({
  providedIn: 'root'
})
export class ChallengeBattleShipService {

  private placedShips = new BehaviorSubject<PlacedShip[]>([]);
  private myShips = new BehaviorSubject<PlacedShip[]>([]);
  private sunkenShips = new BehaviorSubject<PlacedShip[]>([]);
  private mySunkenShips = new BehaviorSubject<PlacedShip[]>([]);
  private board = new BehaviorSubject<string[][]>([]);
  private player = new BehaviorSubject<any>({});
  private ships = new BehaviorSubject<BoardShip[]>([
    { id: 'destroyer', size: 5, orientation: 'horizontal', isPlaced: false },
    { id: 'submarine', size: 4, orientation: 'horizontal', isPlaced: false },
    { id: 'cruiser', size: 3, orientation: 'horizontal', isPlaced: false },
    { id: 'battleship', size: 4, orientation: 'horizontal', isPlaced: false },
    { id: 'carrier', size: 2, orientation: 'horizontal', isPlaced: false }
  ]);
  private shots = new BehaviorSubject<ShotPlaceholder[][]>([]);
  private rivalsShots = new BehaviorSubject<ShotPlaceholder[][]>([]);
  private lastShotStatus = new BehaviorSubject<ShotStatus>({ status: 'unset', message: '' });
  private debouncer: Subject<void> = new Subject<void>();

  ships$ = this.ships.asObservable();
  placedShips$ = this.placedShips.asObservable();
  myShips$ = this.myShips.asObservable();
  board$ = this.board.asObservable();
  player$ = this.player.asObservable();
  shots$ = this.shots.asObservable();
  rivalsShots$ = this.rivalsShots.asObservable();
  sunkenShips$ = this.sunkenShips.asObservable();
  mySunkenShips$ = this.mySunkenShips.asObservable();
  lastShotStatus$ = this.lastShotStatus.asObservable();


  constructor(private cvApiService: CVApiService,
    private cvLocalizationService: CVLocalizationService) {

      this.debouncer.pipe(debounceTime(5000)).subscribe(() => {
        this.lastShotStatus.next({
          status: 'unset',
          message: ''
        });
      });
    }

  setInitialData = () => {
    this.ships.next(this.ships.getValue().map(s => ({...s, orientation: 'horizontal', isPlaced: false})));
    this.placedShips.next([]);
    this.sunkenShips.next([]);
    this.mySunkenShips.next([]);
    this.board.next(Array(8).fill(null).map(() => Array(8).fill('')));
    this.shots.next(Array(8).fill(null).map(() => Array(8).fill({shot: false, hit: false, sunk: false, mark: false})));
    this.rivalsShots.next(Array(8).fill(null).map(() => Array(8).fill({shot: false, hit: false})));
    this.myShips.next([]);
    this.player.next({});
  }

  setAllShipPlaced = () => {
    this.ships.next(this.ships.getValue().map(s => ({...s, orientation: 'horizontal', isPlaced: true })));
    this.shots.next(Array(8).fill(null).map(() => Array(8).fill({shot: false, hit: false, sunk: false, mark: false})));
  }

  getShotPosition(_column: number = 0, _row: number = 0) {
    const col = Math.round((_column - DefaultConfBattleBoat.offsetXPosition) / DefaultConfBattleBoat.cellSize);
    const row = Math.round(_row / DefaultConfBattleBoat.cellSize);
    return { col, row };
  }

  markCellSelected(row: number, col: number, mark: boolean = false) {
    const shots = this.shots.getValue().map(s=> s.map(sd=> ({...sd, mark: false})));
    if(shots[row] && shots[col]){
      shots[row][col] = {
        ...shots[row][col],
        mark
      };
    }
    this.shots.next(shots);
  }

  markShot = (shots: ShotData[]) => {
    const newShots: ShotPlaceholder[][] = Array(8).fill(null).map(() => Array(8).fill({shot: false, hit: false}));
    shots?.forEach(shot => {
      if(newShots[shot?._row][shot?._column]?.shot === false) {
        newShots[shot._row][shot._column] = {
          ...newShots[shot._row][shot._column],
          shot: true,
          hit: shot.Hit
        };
      }
    });
    this.shots.next(newShots);
  };

  updateShots(shot?: ShotData){
    if(!shot) return;
    const row = shot._row;
    const col = shot._column;
    const shots = this.shots.getValue().map(s=> s.map(sd=> ({...sd, mark: false})));
    if(shots[row] && shots[row][col]){
      shots[row][col] = {
        ...shots[row][col],
        hit: shot.Hit,
        shot: true,
        mark: false,
      };
    }
    this.shots.next(shots);
  }

  addShip = (ship: PlacedShip) => {
    const placedS = this.placedShips.getValue();
    this.placedShips.next([...placedS, ship]);
  };

  setPlaceShip = (id: string) => {
    const shipUpdated = this.ships.getValue().map(ship => {
      if(ship.id === id) return {
        ...ship,
        isPlaced: true,
      }
      return ship;
    });
    this.ships.next(shipUpdated);
  };

  mapShipStandar = (s: ShipData) => {
    const [shipType] = Object.entries(ShipTypeObj).find(([key, value])=> value === s.ShipType) || [];
    return {
      id: shipType,
      orientation: !!s.IsVertical ? ORIENTATIONS.vertical: ORIENTATIONS.horizontal,
      position: {
        x: s.StartColumn,
        y: s.StartRow
      },
      size: s.ShipSize,  
    }
  }

  markCell = (i: number, j: number, id: string) => {
    const board = this.board.getValue();
    board[i][j] = id; 
    this.board.next(board);
  };

  getCellPositions = (col: number, row: number, cellSize: number = DefaultConfBattleBoat.cellSize, offsetXPosition: number = DefaultConfBattleBoat.offsetXPosition) => {
    const positionX = (col * cellSize) + offsetXPosition;
    const positionY = (row * cellSize);
    return { positionX, positionY };
  }

  rotateShip = (shipId: TSHIPS_ID) => {
    const shipUpdated = this.ships.getValue().map(s => {
      if(s.id === shipId) return {
        ...s,
        orientation: s.orientation === ORIENTATIONS.horizontal ? ORIENTATIONS.vertical : ORIENTATIONS.horizontal
      }
      return s;
    });
    this.ships.next(shipUpdated);
  };

  markRivalsShots = (shots: ShotData[]) => {
    const newRivalsShots: ShotPlaceholder[][] = Array(8).fill(null).map(() => Array(8).fill({shot: false, hit: false}));
    shots.forEach(shot => {
      if(newRivalsShots[shot._row][shot._column].shot === false) {
        newRivalsShots[shot._row][shot._column] = {
          ...newRivalsShots[shot._row][shot._column],
          shot: true,
          hit: shot.Hit
        };
      }
    });
    this.rivalsShots.next(newRivalsShots);
  };

  async createChallenge(challenge: Challenge): Promise<any> {
    try {
      const url = '/api/v1/gamification/challenges';
      const response = await this.cvApiService.post(url, challenge);
      if (response.status_code >= 200 && response.status_code < 300) {
        this.setInitialData();
        return response.data;
      } else {
        throw response;
      }
    } catch (err) {
      throw err;
    }
  }

  async createShot(shot: {
    challengeId: number,
    _row: number,
    _column: number
  }): Promise<any>{
    try {
      const url = '/api/v1/gamification/battle-boat-shot';
      const response = await this.cvApiService.post<{ shot?: ShotData, ships?: ShipData, player?: TPlayer }>(url, shot);
      if (response.status_code >= 200 && response.status_code < 300) {
        this.updateShots(response.data?.shot);
        const ship = response.data?.ships;
        const player = response.data?.player;
        if(ship){
          const allSunkShips = this.sunkenShips.getValue();
          const newShip = this.mapShipStandar(ship);
          this.sunkenShips.next([...allSunkShips, newShip]);
        }
        if(player) this.player.next(player);
        const shotStatus = +!!response.data?.shot?.Hit as keyof typeof  ShotStatusValues;
        this.lastShotStatus.next({
          status: shotStatus,
          message: shotStatus == 1 ? 'Hit': 'Miss',
        });
        return response.data;
      } else {
        const message = response?.message || 'Error';
        this.lastShotStatus.next({
          status: 2,
          message
        });
        throw response;
      }
    } catch (error) {
      throw error;
    } finally {
      this.debouncer.next();
    }
  }

  async getAllShipsWithShots(challengeId: number){
    try {
      const url = `/api/v1/gamification/my-ships-and-shot/${challengeId}`;
      const response = await this.cvApiService.get<{ ships: ShipData[], shots: any[], rivalsShots: any[], sunkenShips: ShipData[], mySunkenShips: ShipData[], player: any }>(url);
      if (response.status_code >= 200 && response.status_code < 300) {
        const placed: PlacedShip [] = response.data.ships.map(this.mapShipStandar);
        const sunkenShips: PlacedShip [] = response.data.sunkenShips?.map(this.mapShipStandar) || [];
        const mySunkenShips: PlacedShip [] = response.data.mySunkenShips?.map(this.mapShipStandar) || [];
        const player = response.data.player;
        if (response.data.rivalsShots) {
          this.markRivalsShots(response.data.rivalsShots);
        }
        this.setAllShipPlaced();
        this.markShot(response.data.shots);
        this.myShips.next(placed);
        this.sunkenShips.next(sunkenShips);
        this.mySunkenShips.next(mySunkenShips);
        this.player.next(player);
        this.lastShotStatus.next({
          status: 'unset',
          message: '',
        });
        return response.data;
      } else {
        this.setInitialData();
        const message = response?.message || 'Error';
        this.lastShotStatus.next({
          status: 2,
          message
        });
        throw response;
      }
    } catch (error) {
      throw error;
    }
  }

}

export const ShipTypeObj = {
  destroyer: 0,
  submarine:1,
  cruiser:2,
  battleship:3,
  carrier: 4
} as const;

export const ORIENTATIONS = {
  vertical: 'vertical',
  horizontal: 'horizontal',
} as const;

export const DefaultConfBattleBoat = Object.freeze({
  cellSize: 23,
  ShootCellSize: 27,
  offsetXPosition: 3,
  orientation: 'horizontal',
});

export interface PlacedShip {
  id: string;
  size: number;
  orientation: TOrientation;
  position: { x: number, y: number };
}

export type ShipData = {
  ShipId?: number;
  ShipType: number, 
  ShipSize: number,
  StartColumn: number,
  StartRow: number,
  IsVertical: number,
};
